// ============================================================================
// BOOKING EMAILS SERVICE
// Frontend client for account-manager booking emails: admin templates and
// settings, previews/test sends, and each booking's email history.
// ============================================================================

import { api } from './api';

export type BookingEmailType = 'confirmation' | 'update' | 'cancellation' | 'reminder' | 'survey';
export const EMAIL_TYPES: BookingEmailType[] = ['confirmation', 'update', 'cancellation', 'reminder', 'survey'];
export const DEFAULT_TEMPLATE_KEY = 'default';

export const EMAIL_TYPE_LABELS: Record<BookingEmailType, string> = {
  confirmation: 'Confirmation',
  update: 'Update',
  cancellation: 'Cancellation',
  reminder: 'Reminder',
  survey: 'Survey'
};

export const EMAIL_TYPE_HINTS: Record<BookingEmailType, string> = {
  confirmation: 'Sent when the booking is approved.',
  update: 'Sent when the date, room or facilitators of a confirmed booking change.',
  cancellation: 'Sent when a confirmed booking is cancelled.',
  reminder: 'Sent the set number of working days before the session.',
  survey: 'Sent the first working day after the session, with the survey link.'
};

export interface BookingEmailTemplate {
  emailType: BookingEmailType;
  eventTypeId: string;
  subject: string;
  bodyMarkdown: string;
  enabled: boolean;
  updatedByName: string;
  updatedAt: string;
}

export interface BookingEmailSettings {
  reminderWorkingDaysBefore: number;
  reminderSendHour: number;
  surveyUrl: string;
  surveySendHour: number;
  updatedByName?: string;
  updatedAt?: string;
}

export interface TemplatesResponse {
  templates: BookingEmailTemplate[];
  defaults: Record<BookingEmailType, { subject: string; bodyMarkdown: string }>;
  placeholders: { key: string; description: string }[];
  eventTypes: { eventTypeId: string; name: string; isActive: boolean }[];
}

export interface BookingEmailRecord {
  recordKey: string;
  emailType: BookingEmailType;
  status: 'sending' | 'sent' | 'skipped' | 'failed';
  reason?: string;
  to: string[];
  cc: string[];
  blocked: string[];
  subject?: string;
  templateKey?: string;
  attempts: number;
  createdAt: string;
  sentAt?: string;
  error?: string;
  triggeredBy?: string;
}

export interface SampleBooking {
  bookingId: string;
  customer: string;
  startDate: string;
  eventTypeId: string;
  eventTypeName: string;
}

export async function getEmailTemplates(): Promise<TemplatesResponse> {
  return (await api.get<TemplatesResponse>('/api/booking-emails/templates')).data;
}

export async function saveEmailTemplate(
  emailType: BookingEmailType,
  eventTypeId: string,
  input: { subject: string; bodyMarkdown: string; enabled: boolean }
): Promise<BookingEmailTemplate> {
  return (await api.put(`/api/booking-emails/templates/${emailType}/${encodeURIComponent(eventTypeId)}`, input)).data.template;
}

export async function resetEmailTemplate(emailType: BookingEmailType, eventTypeId: string): Promise<void> {
  await api.delete(`/api/booking-emails/templates/${emailType}/${encodeURIComponent(eventTypeId)}`);
}

export async function getEmailSettings(): Promise<BookingEmailSettings> {
  return (await api.get('/api/booking-emails/settings')).data.settings;
}

export async function saveEmailSettings(settings: BookingEmailSettings): Promise<BookingEmailSettings> {
  return (await api.put('/api/booking-emails/settings', settings)).data.settings;
}

export async function listSampleBookings(): Promise<SampleBooking[]> {
  return (await api.get('/api/booking-emails/sample-bookings')).data.bookings;
}

export async function previewEmail(input: {
  emailType: BookingEmailType; subject: string; bodyMarkdown: string; bookingId: string;
}): Promise<{ subject: string; html: string; unknownPlaceholders: string[] }> {
  return (await api.post('/api/booking-emails/preview', input)).data;
}

export async function sendTestEmail(input: {
  emailType: BookingEmailType; subject: string; bodyMarkdown: string; bookingId: string;
}): Promise<{ sentTo: string }> {
  return (await api.post('/api/booking-emails/test', input)).data;
}

export async function listBookingEmails(bookingId: string): Promise<{ emails: BookingEmailRecord[]; maxAttempts: number }> {
  return (await api.get(`/api/studio/bookings/${bookingId}/emails`)).data;
}

export async function retryBookingEmail(bookingId: string, recordKey: string): Promise<BookingEmailRecord> {
  return (await api.post(`/api/studio/bookings/${bookingId}/emails/retry`, { recordKey })).data.email;
}
